import React, { useMemo } from "react";

interface Settlement {
    from: string;
    to: string;
    amount: number;
    currency: string;
}

interface SettlementSummaryCardProps {
    settlements: Settlement[];
}

function formatAmount(value: number, currency: string): string {
    const normalizedCurrency = currency?.trim().toUpperCase() || "CAD";

    try {
        return Number(value).toLocaleString("en-CA", {
            style: "currency",
            currency: normalizedCurrency,
        });
    } catch {
        return `${normalizedCurrency} ${Number(value).toFixed(2)}`;
    }
}

const SettlementSummaryCard: React.FC<SettlementSummaryCardProps> = ({
    settlements,
}) => {
    const settlementsByCurrency = useMemo(() => {
        const groups = new Map<string, Settlement[]>();

        for (const settlement of settlements) {
            const currency =
                settlement.currency?.trim().toUpperCase() || "CAD";

            const existing = groups.get(currency);

            if (existing) {
                existing.push(settlement);
            } else {
                groups.set(currency, [settlement]);
            }
        }

        return Array.from(groups.entries()).sort(
            ([currencyA], [currencyB]) =>
                currencyA.localeCompare(currencyB)
        );
    }, [settlements]);

    return (
        <section className="card mb-4">
            <div className="card-header">
                <h5>Who Owes Whom</h5>
            </div>

            <div className="card-body">
                {settlements.length === 0 ? (
                    <p>All settled up!</p>
                ) : (
                    <>
                        {/* Desktop */}
                        <div className="d-none d-md-block">
                            {settlementsByCurrency.map(
                                ([currency, currencySettlements]) => (
                                    <div
                                        key={currency}
                                        className="mb-4"
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0">
                                                {currency}
                                            </h6>

                                            <strong>
                                                {formatAmount(
                                                    currencySettlements.reduce(
                                                        (total, settlement) =>
                                                            total +
                                                            settlement.amount,
                                                        0
                                                    ),
                                                    currency
                                                )}
                                            </strong>
                                        </div>

                                        <table className="table table-bordered mb-0">
                                            <thead>
                                                <tr>
                                                    <th>From</th>
                                                    <th>To</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {currencySettlements.map(
                                                    (settlement, index) => (
                                                        <tr
                                                            key={`${currency}-${settlement.from}-${settlement.to}-${index}`}
                                                        >
                                                            <td>
                                                                {settlement.from}
                                                            </td>
                                                            <td>
                                                                {settlement.to}
                                                            </td>
                                                            <td>
                                                                <strong>
                                                                    {formatAmount(
                                                                        settlement.amount,
                                                                        currency
                                                                    )}
                                                                </strong>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            )}
                        </div>

                        {/* Mobile */}
                        <div className="d-md-none">
                            {settlementsByCurrency.map(
                                ([currency, currencySettlements]) => (
                                    <div
                                        key={currency}
                                        className="mb-4"
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0">
                                                {currency}
                                            </h6>

                                            <strong>
                                                {formatAmount(
                                                    currencySettlements.reduce(
                                                        (total, settlement) =>
                                                            total +
                                                            settlement.amount,
                                                        0
                                                    ),
                                                    currency
                                                )}
                                            </strong>
                                        </div>

                                        {currencySettlements.map(
                                            (settlement, index) => (
                                                <div
                                                    key={`${currency}-mobile-${settlement.from}-${settlement.to}-${index}`}
                                                    className="card shadow-sm mb-3 p-3"
                                                >
                                                    <p className="mb-1">
                                                        <strong>From:</strong>{" "}
                                                        {settlement.from}
                                                    </p>

                                                    <p className="mb-1">
                                                        <strong>To:</strong>{" "}
                                                        {settlement.to}
                                                    </p>

                                                    <p className="mb-0">
                                                        <strong>
                                                            Amount:
                                                        </strong>{" "}
                                                        {formatAmount(
                                                            settlement.amount,
                                                            currency
                                                        )}
                                                    </p>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default SettlementSummaryCard;